import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { getBanners } from "../../../../api/calls/client/banners";
import { getProducts } from "../../../../api/calls/product/products";
import { getCategories } from "../../../../api/calls/product/categories";
import { getProductsAttributes } from "../../../../api/calls/product/get_products_attributes";
import { add_product_thunk } from "../../../../reducers/slices/basket/thunks/add_product_thunk";
import BGImage from "../../../../assets/bgImage.png";
const defaultPagination = {
    currentPage: 1,
    totalRecords: 0,
    recordsPerPage: 100,
};
const SHOP_BANNER_PAGE_SIZE = 10;
const SHOP_CAROUSEL_CUSTOM_ID = "shop-carousel";
const CATEGORY_LOOKUP_PAGE = 1;
const CATEGORY_LOOKUP_SIZE = 100;
const DEFAULT_BANNER_SLIDE = {
    _id: "first-slide",
    title: "Heading",
    subtitle: "Welcome to SCEE",
    imageUrl: BGImage,
    layout: "cover",
};
export function useShopState() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { categoryId, categoryName } = useParams();
    const [searchParams] = useSearchParams();
    const searchQuery = (searchParams.get("search") ?? "").trim();
    const [products, setProducts] = useState([]);
    const [categories, setCategories] = useState([]);
    const [productAttributes, setProductAttributes] = useState([]);
    const [activeIndex, setActiveIndex] = useState(0);
    const [slideDirection, setSlideDirection] = useState(1);
    const [slides, setSlides] = useState([DEFAULT_BANNER_SLIDE]);
    const [bannerPage, setBannerPage] = useState(0);
    const [hasMoreBanners, setHasMoreBanners] = useState(true);
    const [pagination, setPagination] = useState(defaultPagination);
    const [loadingProducts, setLoadingProducts] = useState(true);
    const [loadingCategories, setLoadingCategories] = useState(true);
    const [loadingBanners, setLoadingBanners] = useState(true);
    const [isFiltering, setIsFiltering] = useState(false);
    const [loadingFilters, setLoadingFilters] = useState(true);
    const { filter } = useSelector((state) => state.selectionState);
    const isFetchingBannerRef = useRef(false);
    const slidesRef = useRef(slides);
    const activeIndexRef = useRef(activeIndex);
    const bannerPageRef = useRef(bannerPage);
    const hasMoreBannersRef = useRef(hasMoreBanners);
    const preloadedImageUrlsRef = useRef(new Set());
    useEffect(() => {
        slidesRef.current = slides;
    }, [slides]);
    useEffect(() => {
        activeIndexRef.current = activeIndex;
    }, [activeIndex]);
    useEffect(() => {
        bannerPageRef.current = bannerPage;
    }, [bannerPage]);
    useEffect(() => {
        hasMoreBannersRef.current = hasMoreBanners;
    }, [hasMoreBanners]);
    const applyDefaultBannerSlide = useCallback(() => {
        setSlides([DEFAULT_BANNER_SLIDE]);
        slidesRef.current = [DEFAULT_BANNER_SLIDE];
        setActiveIndex(0);
        activeIndexRef.current = 0;
        setBannerPage(0);
        bannerPageRef.current = 0;
        setHasMoreBanners(false);
        hasMoreBannersRef.current = false;
    }, []);
    const mapBannerToSlide = useCallback((banner) => {
        return {
            _id: banner._id,
            title: banner.title || "Heading",
            subtitle: banner.description || "Welcome to SCEE",
            imageUrl: banner.image || BGImage,
            layout: "cover",
            label: banner.label,
            customId: banner.custom_id,
            linkType: banner.link_type,
            linkTarget: banner.link_target,
            createdAt: banner.createdAt,
            updatedAt: banner.updatedAt,
        };
    }, []);
    const preloadSlideImages = useCallback((slidesToPreload) => {
        for (const slide of slidesToPreload) {
            const imageUrl = slide.imageUrl;
            if (!imageUrl || preloadedImageUrlsRef.current.has(imageUrl)) {
                continue;
            }
            const img = new Image();
            img.src = imageUrl;
            preloadedImageUrlsRef.current.add(imageUrl);
        }
    }, []);
    const isBannerVisibleNow = useCallback((banner) => {
        if (banner.custom_id !== SHOP_CAROUSEL_CUSTOM_ID) {
            return false;
        }
        const now = Date.now();
        const startTime = banner.start_date ? new Date(banner.start_date).getTime() : null;
        const endTime = banner.end_date ? new Date(banner.end_date).getTime() : null;
        if (startTime !== null && Number.isFinite(startTime) && now < startTime) {
            return false;
        }
        if (endTime !== null && Number.isFinite(endTime) && now > endTime) {
            return false;
        }
        return true;
    }, []);
    const fetchBannerPage = useCallback(async (page) => {
        if (isFetchingBannerRef.current) {
            return {
                slides: slidesRef.current,
                hasMore: hasMoreBannersRef.current,
                success: false,
            };
        }
        isFetchingBannerRef.current = true;
        setLoadingBanners(true);
        try {
            const response = await getBanners({
                active: true,
                sort_by: "sort_order",
                order_by: "asc",
                custom_id: SHOP_CAROUSEL_CUSTOM_ID,
                page: page.toString(),
                size: SHOP_BANNER_PAGE_SIZE.toString(),
            });
            if (!response.success) {
                return {
                    slides: slidesRef.current,
                    hasMore: hasMoreBannersRef.current,
                    success: false,
                };
            }
            const rawBanners = response.content?.banners ?? [];
            const visibleSlides = rawBanners.filter(isBannerVisibleNow).map(mapBannerToSlide);
            preloadSlideImages(visibleSlides);
            const existingSlides = slidesRef.current.filter((slide) => slide._id !== DEFAULT_BANNER_SLIDE._id);
            const seenIds = new Set(existingSlides.map((slide) => slide._id));
            const mergedSlides = [...existingSlides];
            for (const slide of visibleSlides) {
                if (!seenIds.has(slide._id)) {
                    mergedSlides.push(slide);
                    seenIds.add(slide._id);
                }
            }
            const totalRecords = Number(response.content?.count ?? 0);
            const totalPages = totalRecords > 0 ? Math.ceil(totalRecords / SHOP_BANNER_PAGE_SIZE) : 0;
            const resolvedHasMore = totalPages > 0 ? page < totalPages : rawBanners.length === SHOP_BANNER_PAGE_SIZE;
            const nextSlides = mergedSlides.length > 0 ? mergedSlides : slidesRef.current;
            setSlides(nextSlides);
            slidesRef.current = nextSlides;
            setBannerPage(page);
            bannerPageRef.current = page;
            setHasMoreBanners(resolvedHasMore);
            hasMoreBannersRef.current = resolvedHasMore;
            return {
                slides: nextSlides,
                hasMore: resolvedHasMore,
                success: true,
            };
        }
        finally {
            setLoadingBanners(false);
            isFetchingBannerRef.current = false;
        }
    }, [isBannerVisibleNow, mapBannerToSlide, preloadSlideImages]);
    useEffect(() => {
        const fetchInitialBanners = async () => {
            const result = await fetchBannerPage(1);
            if (!result.success && slidesRef.current.length === 0) {
                applyDefaultBannerSlide();
            }
        };
        void fetchInitialBanners();
        fetchAttributes();
        fetchCategories();
    }, [applyDefaultBannerSlide, fetchBannerPage]);
    // Detect if the filter object values have changed, then refetch products.
    useEffect(() => {
        if (Object.keys(filter).length > 0) {
            fetchProducts();
        }
    }, [filter]);
    useEffect(() => {
        fetchProducts();
    }, [categoryId, pagination.currentPage, searchQuery]);
    useEffect(() => {
        setPagination((prev) => (prev.currentPage === 1 ? prev : { ...prev, currentPage: 1 }));
    }, [searchQuery]);
    const fetchCategories = async () => {
        const { success, content } = await getCategories({ page: CATEGORY_LOOKUP_PAGE.toString(), size: CATEGORY_LOOKUP_SIZE.toString() });
        if (success) {
            const { product_category_list } = content;
            const filtered_list = product_category_list?.filter((category) => !!category.is_online);
            setCategories(filtered_list);
            setLoadingCategories(false);
        }
        else {
            setCategories([]);
            setLoadingCategories(false);
        }
    };
    const fetchProducts = async () => {
        setIsFiltering(true);
        let attributesForFiltering = [];
        if (filter && Object.keys(filter).length > 0) {
            for (const key of Object.keys(filter)) {
                if (filter[key].length > 0) {
                    attributesForFiltering.push({ name: key, value: filter[key] });
                }
            }
        }
        // Always sort by barcode ascending, with or without filters applied,
        // so the ordering stays consistent when filters are added
        const sortBy = "barcode"; // Product barcode field (e.g., "406010A", "406210A")
        const orderBy = "1"; // Ascending order (backend will convert to number)
        const { success, content: { product_list, count }, } = await getProducts({
            page: pagination.currentPage.toString(),
            size: pagination.recordsPerPage.toString(),
            attributes: attributesForFiltering,
            ...(categoryId && { category_id: categoryId }),
            ...(sortBy && { sort_by: sortBy }),
            ...(orderBy && { order_by: orderBy }),
            ...(searchQuery && { search_string: searchQuery }),
        });
        if (success) {
            setProducts(product_list || []);
            setPagination({
                ...pagination,
                totalRecords: parseInt(count || "0"),
            });
            setLoadingProducts(false);
            setIsFiltering(false);
        }
        else {
            setProducts([]);
            setLoadingProducts(false);
            setIsFiltering(false);
        }
    };
    const fetchAttributes = async () => {
        const { success, content } = await getProductsAttributes({ type: "LIST" });
        if (success) {
            const { product_attribute_list } = content;
            const groupedAttributes = {};
            for (const attribute of product_attribute_list) {
                if (!groupedAttributes[attribute.name]) {
                    groupedAttributes[attribute.name] = [];
                }
                if (attribute.options && Array.isArray(attribute.options)) {
                    groupedAttributes[attribute.name].push(...attribute.options);
                }
            }
            const formattedAttributes = Object.keys(groupedAttributes).map((key) => ({
                name: key,
                list: groupedAttributes[key],
            }));
            formattedAttributes.sort((a, b) => a.name.localeCompare(b.name));
            setProductAttributes(formattedAttributes);
            setLoadingFilters(false);
        }
        else {
            setLoadingFilters(false);
        }
    };
    const handleNext = useCallback(() => {
        if (loadingBanners) {
            return;
        }
        setSlideDirection(1);
        const loadedSlides = slidesRef.current;
        if (loadedSlides.length === 0) {
            return;
        }
        const current = activeIndexRef.current;
        if (current < loadedSlides.length - 1) {
            setActiveIndex(current + 1);
            return;
        }
        const loadNextPageIfNeeded = async () => {
            if (hasMoreBannersRef.current) {
                let previousLength = slidesRef.current.length;
                let nextPage = bannerPageRef.current;
                while (hasMoreBannersRef.current) {
                    nextPage += 1;
                    const result = await fetchBannerPage(nextPage);
                    if (!result.success) {
                        break;
                    }
                    if (result.slides.length > previousLength) {
                        setActiveIndex(previousLength);
                        return;
                    }
                    previousLength = result.slides.length;
                }
            }
            setActiveIndex(0);
        };
        void loadNextPageIfNeeded();
    }, [fetchBannerPage, loadingBanners]);
    const handlePrev = useCallback(() => {
        if (loadingBanners) {
            return;
        }
        setSlideDirection(-1);
        const loadedSlides = slidesRef.current;
        if (loadedSlides.length === 0) {
            return;
        }
        const current = activeIndexRef.current;
        setActiveIndex(current === 0 ? loadedSlides.length - 1 : current - 1);
    }, [loadingBanners]);
    const handleCarouselDialSelected = useCallback((targetSlideIndex) => {
        if (loadingBanners || targetSlideIndex < 0 || targetSlideIndex === activeIndexRef.current) {
            return;
        }
        const current = activeIndexRef.current;
        setSlideDirection(targetSlideIndex > current ? 1 : -1);
        const loadedSlides = slidesRef.current;
        if (targetSlideIndex < loadedSlides.length) {
            setActiveIndex(targetSlideIndex);
            return;
        }
        const loadUntilTarget = async () => {
            let nextPage = bannerPageRef.current;
            while (targetSlideIndex >= slidesRef.current.length && hasMoreBannersRef.current) {
                nextPage += 1;
                await fetchBannerPage(nextPage);
            }
            if (targetSlideIndex < slidesRef.current.length) {
                setActiveIndex(targetSlideIndex);
            }
        };
        void loadUntilTarget();
    }, [fetchBannerPage, loadingBanners]);
    const handleBannerClicked = useCallback(() => {
        const activeSlide = slidesRef.current[activeIndexRef.current];
        const linkTarget = activeSlide?.linkTarget?.trim();
        const linkType = activeSlide?.linkType?.toLowerCase();
        if (!linkTarget) {
            return;
        }
        if (linkType?.includes("category")) {
            const matchingCategory = categories.find((category) => category?._id === linkTarget || category?.identifier === linkTarget);
            const resolvedCategoryName = matchingCategory?.name?.trim();
            if (resolvedCategoryName) {
                navigate(`/shop/${linkTarget}/${encodeURI(resolvedCategoryName)}`);
            }
            else {
                navigate(`/shop/${linkTarget}`);
            }
            return;
        }
        if (linkType?.includes("product")) {
            navigate(`/product/${linkTarget}`);
            return;
        }
        if (/^https?:\/\//i.test(linkTarget)) {
            window.open(linkTarget, "_blank", "noopener,noreferrer");
            return;
        }
        navigate(linkTarget.startsWith("/") ? linkTarget : `/${linkTarget}`);
    }, [categories, navigate]);
    const handleCheckout = useCallback(() => {
        navigate("/checkout");
    }, [navigate]);
    const handleSubmit = useCallback(({ product_list }) => {
        const filteredProducts = product_list?.filter(({ quantity }) => !!quantity && quantity >= 1) ?? [];
        for (const product of filteredProducts) {
            dispatch(add_product_thunk({ product, quantity: product?.quantity, selectedVariations: null }));
        }
    }, [dispatch]);
    const handlePaginationChanged = useCallback((page) => {
        setPagination({
            ...pagination,
            currentPage: page,
        });
    }, [pagination]);
    return useMemo(() => ({
        activeIndex,
        slideDirection,
        categories,
        productAttributes,
        handleCheckout,
        handleNext,
        handlePrev,
        handleCarouselDialSelected,
        handleBannerClicked,
        handleSubmit,
        handlePaginationChanged,
        products,
        slides,
        loadingProducts,
        loadingCategories,
        loadingBanners,
        loadingFilters,
        isFiltering,
        pagination,
        categoryName,
        searchQuery,
    }), [
        activeIndex,
        slideDirection,
        categories,
        productAttributes,
        handleCheckout,
        handleNext,
        handlePrev,
        handleCarouselDialSelected,
        handleBannerClicked,
        handleSubmit,
        handlePaginationChanged,
        products,
        slides,
        loadingProducts,
        loadingCategories,
        loadingBanners,
        loadingFilters,
        isFiltering,
        pagination,
        categoryName,
        searchQuery,
    ]);
}
